/**
 * Compact header logo on scroll (snippets/kavacha-logo-scroll.liquid).
 *
 * Past `data-threshold` pixels the header section gets `data-kv-logo="compact"`, and CSS moves the
 * progress value --kv-p from 0 to 1. It returns to the full logo below half the threshold, so the
 * state cannot flicker at the boundary.
 *
 * Layout stays still while the header shrinks:
 * - the sticky section reserves its natural landing height while the inner header changes size;
 * - page content keeps the landing `--header-height` and `--header-group-height` (body attribute
 *   `data-kv-logo-compact`, read by the snippet's CSS), so heroes and first-section offsets do not
 *   resize. The live header height stays available as `--kv-hh-live` for sticky product details.
 *
 * Only runs when the header is sticky; without a sticky header there is nothing to compact.
 */

const SQUEEZE_QUERY = window.matchMedia('(min-width: 990px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

class KavachaLogoScroll extends HTMLElement {
  /** @type {HTMLElement | null} */
  #header = null;
  /** @type {HTMLElement | null} */
  #section = null;
  #compact = false;
  #threshold = 100;
  /** @type {number | null} */
  #raf = null;
  /** @type {number | null} */
  #releaseTimer = null;
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;
  /** @type {EventTarget[]} */
  #targets = [];

  connectedCallback() {
    this.#header = this.closest('header-component');
    if (!this.#header || !this.#header.hasAttribute('sticky')) return;

    this.#section = this.#header.closest('.header-section') ?? this.#header;
    this.#threshold = Math.max(1, Number(this.dataset.threshold) || 100);
    this.#measureFlowHeight();

    const pageWrapper = document.querySelector('.page-wrapper');
    this.#targets = pageWrapper ? [window, pageWrapper] : [window];
    for (const target of this.#targets) target.addEventListener('scroll', this.#onScroll, { passive: true });
    window.addEventListener('resize', this.#onResize, { passive: true });

    this.#resizeObserver = new ResizeObserver(([entry]) => {
      const size = entry?.borderBoxSize?.[0]?.blockSize;
      if (size) document.body.style.setProperty('--kv-hh-live', `${Math.round(size)}px`);
      if (!this.#compact && this.#releaseTimer === null) this.#measureFlowHeight();
    });
    this.#resizeObserver.observe(this.#header);

    this.#update();
  }

  disconnectedCallback() {
    for (const target of this.#targets) target.removeEventListener('scroll', this.#onScroll);
    window.removeEventListener('resize', this.#onResize);
    this.#resizeObserver?.disconnect();
    if (this.#raf !== null) cancelAnimationFrame(this.#raf);
    if (this.#releaseTimer !== null) clearTimeout(this.#releaseTimer);
    this.#section?.removeAttribute('data-kv-logo');
    this.#section?.style.removeProperty('--kv-flow-height');
    document.body.removeAttribute('data-kv-logo-compact');
    this.#compact = false;
  }

  #onScroll = () => {
    if (this.#raf !== null) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = null;
      this.#update();
    });
  };

  #onResize = () => {
    if (this.#releaseTimer === null) this.#measureFlowHeight();
  };

  #scrollTop() {
    const pageWrapper = document.querySelector('.page-wrapper');
    if (SQUEEZE_QUERY.matches && pageWrapper && pageWrapper.scrollHeight > pageWrapper.clientHeight) {
      return pageWrapper.scrollTop;
    }
    return (document.scrollingElement ?? document.documentElement).scrollTop;
  }

  #update() {
    const top = this.#scrollTop();
    const next = this.#compact ? top > this.#threshold / 2 : top > this.#threshold;
    if (next !== this.#compact) this.#set(next);
  }

  /**
   * Measures the section's natural landing height without its reservation or the compact state, then
   * restores the current state before the browser paints. The transparent home header has height zero.
   */
  #measureFlowHeight() {
    const section = this.#section;
    if (!section) return;
    const wasCompact = section.dataset.kvLogo === 'compact';
    section.setAttribute('data-kv-logo-instant', '');
    section.style.removeProperty('--kv-flow-height');
    section.removeAttribute('data-kv-logo');
    const full = section.getBoundingClientRect().height;
    if (wasCompact) section.dataset.kvLogo = 'compact';
    section.style.setProperty('--kv-flow-height', `${full}px`);
    void section.offsetHeight;
    section.removeAttribute('data-kv-logo-instant');
  }

  /** @param {boolean} compact */
  #set(compact) {
    const section = this.#section;
    if (!section) return;
    this.#compact = compact;
    if (this.#releaseTimer !== null) {
      clearTimeout(this.#releaseTimer);
      this.#releaseTimer = null;
    }

    if (compact) {
      const body = document.body;
      if (!body.hasAttribute('data-kv-logo-compact')) {
        const styles = getComputedStyle(body);
        body.style.setProperty('--kv-hh-full', styles.getPropertyValue('--header-height').trim() || '0px');
        body.style.setProperty('--kv-hgh-full', styles.getPropertyValue('--header-group-height').trim() || '0px');
        body.setAttribute('data-kv-logo-compact', '');
      }
      section.dataset.kvLogo = 'compact';
      return;
    }

    section.removeAttribute('data-kv-logo');
    // Page content goes back to the live header height once the header has grown back.
    this.#releaseTimer = window.setTimeout(
      () => {
        this.#releaseTimer = null;
        if (!this.#compact) {
          document.body.removeAttribute('data-kv-logo-compact');
          this.#measureFlowHeight();
        }
      },
      REDUCED_MOTION.matches ? 0 : 640
    );
  }
}

if (!customElements.get('kavacha-logo-scroll')) {
  customElements.define('kavacha-logo-scroll', KavachaLogoScroll);
}
